import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CARD_SECTION_TITLE_CLASS,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  TextButton,
} from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { usersApi, extractApiError, type UpdateProfileRequest } from '../../lib/users';
import { getUserIdentifierFromUser, authApi, AuthMethod } from '../../lib/auth';
import ChangePasswordModal from '../../components/modals/ChangePasswordModal';
import AccountMergeModal from '../../components/modals/AccountMergeModal';
import { DirtyHint } from '../../components/common/DirtyHint';
import PhoneVerification from '../../components/PhoneVerification';
import EmailVerification from '../../components/EmailVerification';
import { formatDateToLocal } from '@safetyhub/shared';

const FIVE_MINUTES_MS = 300000; // 5분

interface VerifiedEmailFieldProps {
  /**
   * 섹션 라벨. dt 텍스트 · 인증 컴포넌트 라벨 · 해제 버튼 문구(`{label} 등록 해제`) ·
   * 저장 실패 토스트 문구가 모두 여기서 파생된다.
   */
  label: string;
  /** 저장된 값. 비면 "미등록"이 표시되고 해제 버튼도 감춘다. */
  value: string | null;
  /** 값 아래 덧붙는 설명. 없으면 `<p>` 자체를 렌더하지 않는다. */
  hint?: string;
  /** 저장 중 잠금 — 페이지 전역 상태다(이름·전화번호 등 다른 칸도 같이 잠긴다). */
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
  /** 저장·해제 성공 후 사용자 재조회. */
  onSaved: () => Promise<void>;
  showToast: (message: string, type: 'error') => void;
  /**
   * 인증 완료 저장 payload — **두 섹션이 갈리는 유일한 지점**(email vs contactEmail).
   * 계정 이메일은 탈퇴 복원·비밀번호 재설정의 생명선이고, 연락처 이메일은 결재 토큰이
   * 실제로 가는 주소라, 둘 다 등록·변경에 소유권 증명(인증 토큰)을 요구한다
   * (결정 기록 2026-08-23-contact-email-redefine-and-source-axis-removal).
   */
  buildSavePayload: (email: string, verificationToken: string) => UpdateProfileRequest;
  /**
   * 해제 payload — 해제는 소유권 주장이 아니므로 인증 불요.
   * `null`을 보내야 서버 삭제 분기를 탄다(`''`는 DTO `@IsEmail`에 걸려 400).
   */
  clearPayload: UpdateProfileRequest;
}

/**
 * 인증 코드로 소유권을 증명해야 저장되는 이메일 칸.
 *
 * 계정 이메일(USERNAME 전용)과 연락처 이메일이 라벨·payload 키·설명 문구만 다른 같은 흐름이라
 * 한 컴포넌트로 접었다 — 종전에는 핸들러 2쌍 + JSX 2벌이 나란히 복제돼 있었다.
 */
function VerifiedEmailField({
  label,
  value,
  hint,
  isSaving,
  setIsSaving,
  onSaved,
  showToast,
  buildSavePayload,
  clearPayload,
}: VerifiedEmailFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  // 인증 완료 시 저장 — 등록·변경은 인증 토큰이 필수다(전화번호와 대칭).
  const handleVerified = async (verificationToken: string) => {
    setIsSaving(true);
    try {
      await usersApi.updateProfile(buildSavePayload(draft.trim(), verificationToken));
      await onSaved();
      setIsEditing(false);
    } catch (err) {
      // ⚠️ 토스트만 띄우면 안 된다. `EmailVerification`은 이미 step='done'이라 초록
      // "인증완료" 배지와 그 주소가 화면에 남는데, **저장은 실패했다.** 토스트는 사라지므로
      // 화면만 보면 등록된 것처럼 읽힌다. 게다가 토큰은 소진돼 그 자리에서 재시도할 수도
      // 없다 — 편집 상태를 접어 원래 값이 보이게 하고, 무엇을 해야 하는지 말해 준다.
      const apiError = extractApiError(err);
      showToast(
        `${apiError.message} ${label}이 저장되지 않았습니다. 인증을 다시 진행해 주세요.`,
        'error',
      );
      setIsEditing(false);
      setDraft('');
    } finally {
      setIsSaving(false);
    }
  };

  // 등록 해제 — 인증 불요(위 clearPayload 주석 참조).
  const handleClear = async () => {
    setIsSaving(true);
    try {
      await usersApi.updateProfile(clearPayload);
      await onSaved();
      setIsEditing(false);
      setDraft('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="py-2 border-t border-line-soft">
      {isEditing ? (
        <div className="space-y-3">
          {/* 등록·변경은 인증 코드 확인 후 자동 저장 (전화번호 편집과 같은 흐름) */}
          <EmailVerification
            label={label}
            email={draft}
            onEmailChange={setDraft}
            onVerified={handleVerified}
            disabled={isSaving}
          />
          <div className="flex items-center justify-between">
            {value ? (
              <TextButton
                tone="danger"
                onClick={handleClear}
                disabled={isSaving}
                className="text-sm"
              >
                {label} 등록 해제
              </TextButton>
            ) : (
              <span />
            )}
            <Button
              size="sm"
              onClick={() => { setIsEditing(false); setDraft(value || ''); }}
              disabled={isSaving}
            >
              취소
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <dt className="text-sm font-medium text-ink-500">{label}</dt>
            <dd className="mt-1 text-sm text-ink-900">
              {value || <span className="text-ink-400">미등록</span>}
            </dd>
            {hint && <p className="mt-1 text-xs text-ink-400">{hint}</p>}
          </div>
          <TextButton
            onClick={() => { setDraft(value || ''); setIsEditing(true); }}
            className="text-sm font-medium"
          >
            {value ? '수정' : '등록'}
          </TextButton>
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const { user, fetchUser, currentCompanyId, currentMembership } = useAuthStore();
  const { showToast } = useToastStore();

  // 편집 상태
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [editPhone, setEditPhone] = useState(user?.phone?.replace(/-/g, '') || '');
  // 이메일 두 칸(계정·연락처)의 편집 상태는 `VerifiedEmailField`가 각자 소유한다.
  const [isEditingRank, setIsEditingRank] = useState(false);
  // rank는 현재 회사(currentMembership) 기준 — 멀티회사 멤버에서 primary CM 불일치 방지
  const [editRank, setEditRank] = useState(currentMembership?.rank || '');
  const [isSaving, setIsSaving] = useState(false);

  // 모달 상태
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  // 소셜 로그인 해제 상태
  const [unlinkingAuthId, setUnlinkingAuthId] = useState<string | null>(null);
  const [unlinkPassword, setUnlinkPassword] = useState('');
  const [isUnlinking, setIsUnlinking] = useState(false);

  // 연결된 인증 수단 조회
  const { data: authMethods, refetch: refetchAuthMethods } = useQuery({
    queryKey: ['auth', 'methods'],
    queryFn: authApi.getAuthMethods,
    staleTime: FIVE_MINUTES_MS,
  });

  // 인라인 편집 dirty 게이트 — 칸마다 한 필드뿐이라 기준점(저장된 값)이 늘 손에 있다. 그래서
  // useDirtySnapshot의 capture 대신 원본과 직접 비교한다(같은 "기준값 vs 현재값" 판정이다).
  // 비교는 저장이 실제로 보내는 형태(trim)로 — 공백만 더한 것은 변경이 아니다.
  const isNameDirty = editName.trim() !== (user?.name ?? '');
  const isRankDirty = editRank.trim() !== (currentMembership?.rank ?? '');

  // 날짜 포맷
  const formatDate = (dateString: string) => {
    return formatDateToLocal(new Date(dateString));
  };

  // 전화번호 포맷
  const formatPhone = (phone: string) => {
    if (!phone) return '-';
    if (phone.length === 11) {
      return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
    }
    return phone;
  };

  // 인증 제공자 라벨
  const getProviderLabel = (provider: AuthMethod['provider']) => {
    switch (provider) {
      case 'EMAIL':
        return '이메일';
      case 'ID':
        return '아이디';
      case 'KAKAO':
        return '카카오';
      case 'GOOGLE':
        return '구글';
      default:
        return provider;
    }
  };

  // 인증 제공자 아이콘/색상
  const getProviderStyle = (provider: AuthMethod['provider']) => {
    switch (provider) {
      case 'EMAIL':
        return 'bg-blue-100 text-blue-600';
      case 'ID':
        return 'bg-ink-100 text-ink-600';
      case 'KAKAO':
        return 'bg-yellow-100 text-yellow-700';
      case 'GOOGLE':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-ink-100 text-ink-600';
    }
  };

  // 소셜 로그인 여부
  const isSocialAuth = (provider: AuthMethod['provider']) => {
    return provider === 'KAKAO' || provider === 'GOOGLE';
  };

  // 비밀번호 인증이 있는지 확인
  const hasPasswordAuth = authMethods?.some(
    (m) => m.provider === 'EMAIL' || m.provider === 'ID'
  );

  // 소셜 로그인 해제 가능 여부 (최소 1개 인증 수단 유지)
  const canUnlink = (authMethods?.length || 0) > 1;

  // 소셜 로그인 해제
  const handleUnlink = async () => {
    if (!unlinkingAuthId) return;

    setIsUnlinking(true);
    try {
      await authApi.unlinkAuthMethod(unlinkingAuthId, hasPasswordAuth ? unlinkPassword : undefined);
      await refetchAuthMethods();
      // 성공 토스트는 서버에서 ActionResultHelper를 통해 전송됨
      setUnlinkingAuthId(null);
      setUnlinkPassword('');
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsUnlinking(false);
    }
  };

  // 해제 취소
  const cancelUnlink = () => {
    setUnlinkingAuthId(null);
    setUnlinkPassword('');
  };

  // 이름 저장
  const handleSaveName = async () => {
    // Enter 키 경로도 버튼과 같은 게이트를 탄다 — 무변경 저장은 보내지 않는다.
    if (!isNameDirty) return;
    if (!editName.trim() || editName.trim().length < 2) {
      showToast('이름은 2자 이상 입력해주세요.', 'error');
      return;
    }

    setIsSaving(true);
    try {
      await usersApi.updateProfile({ name: editName.trim() });
      await fetchUser();
      setIsEditingName(false);
      // 성공 토스트는 WebSocket ACTION_RESULT로 처리됨
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // 전화번호 인증 완료 시 저장
  const handlePhoneVerified = async (verificationToken: string) => {
    // 인증 완료 후 자동 저장
    const normalizedPhone = editPhone.replace(/-/g, '');
    setIsSaving(true);
    try {
      await usersApi.updateProfile({
        phone: normalizedPhone,
        phoneVerificationToken: verificationToken,
      });
      await fetchUser();
      setIsEditingPhone(false);
      // 성공 토스트는 WebSocket ACTION_RESULT로 처리됨
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // 전화번호 편집 취소
  const handleCancelPhoneEdit = () => {
    setIsEditingPhone(false);
    setEditPhone(user?.phone?.replace(/-/g, '') || '');
  };

  // 직급 저장
  const handleSaveRank = async () => {
    if (!isRankDirty) return;
    setIsSaving(true);
    try {
      // currentCompanyId 동봉 → 서버에서 현재 회사 스코프 CM에 저장
      await usersApi.updateProfile({
        rank: editRank.trim(),
        ...(currentCompanyId && { companyId: currentCompanyId }),
      });
      await fetchUser();
      setIsEditingRank(false);
    } catch (err) {
      const apiError = extractApiError(err);
      showToast(apiError.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // 비밀번호 변경 성공
  const handlePasswordChangeSuccess = () => {
    // 성공 토스트는 WebSocket ACTION_RESULT로 처리됨
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-ink-500">로그인이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">내 프로필</h1>
        <p className="mt-1 text-[15px] font-medium text-ink-500">
          개인 정보를 확인하고 수정합니다.
        </p>
      </div>

      {/* 넓은 화면에서는 신원·로그인 수단을 본문에 두고, 계정 조작은 오른쪽 rail로 모은다. */}
      <div className="space-y-6 lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0">
        <div className="lg:col-span-2 space-y-6">
      {/* 내 정보 섹션 */}
      <Card padding="none" radius="lg" elevation="md">
        <div className="px-6 py-4 border-b border-line">
          <h2 className={CARD_SECTION_TITLE_CLASS}>내 정보</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* 이름 */}
          <div className="flex items-center justify-between py-2">
            <div className="flex-1">
              <dt className="text-sm font-medium text-ink-500">이름</dt>
              {isEditingName ? (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editName}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing && !isSaving) {
                          handleSaveName();
                        } else if (e.key === 'Escape') {
                          setIsEditingName(false);
                          setEditName(user.name);
                        }
                      }}
                      onChange={(e) => setEditName(e.target.value)}
                      className={`${FORM_CONTROL_CLASS} flex-1`}
                      autoFocus
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveName}
                      disabled={isSaving || !isNameDirty}
                    >
                      저장
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setIsEditingName(false);
                        setEditName(user.name);
                      }}
                    >
                      취소
                    </Button>
                  </div>
                  {/* 저장이 왜 비활성인지 말해준다 — 인라인 칸이라 푸터 대신 입력 아래 한 줄로 둔다. */}
                  <DirtyHint isDirty={isNameDirty} className="mt-1 block text-xs" />
                </div>
              ) : (
                <dd className="mt-1 text-sm text-ink-900">{user.name}</dd>
              )}
            </div>
            {!isEditingName && (
              <TextButton
                onClick={() => {
                  setEditName(user.name);
                  setIsEditingName(true);
                }}
                className="text-sm font-medium"
              >
                수정
              </TextButton>
            )}
          </div>

          {/* 아이디/이메일 */}
          <div className="flex items-center justify-between py-2 border-t border-line-soft">
            <div>
              <dt className="text-sm font-medium text-ink-500">
                {user.accountType === 'USERNAME' ? '아이디' : '이메일'}
              </dt>
              <dd className="mt-1 text-sm text-ink-900">
                {getUserIdentifierFromUser(user)}
              </dd>
            </div>
            {/* 아이디/이메일은 수정 불가 */}
          </div>

          {/* 이메일 (USERNAME 계정 전용) — 계정 이메일은 탈퇴 복원·비밀번호 재설정의
              유일한 생명선이라 소유권 증명 없이는 등록할 수 없다. */}
          {user.accountType === 'USERNAME' && (
            <VerifiedEmailField
              label="이메일"
              value={user.email}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
              onSaved={fetchUser}
              showToast={showToast}
              buildSavePayload={(email, verificationToken) => ({
                email,
                emailVerificationToken: verificationToken,
              })}
              clearPayload={{ email: null }}
            />
          )}

          {/* 연락처 이메일 (member.email) — 전 계정 유형 공통.
              계정 이메일과 다른 축이다: 이쪽이 결재 요청·알림이 실제로 가는 주소라
              ID 계정처럼 계정 이메일이 없어도 독립적으로 등록할 수 있어야 한다. */}
          <VerifiedEmailField
            label="연락처 이메일"
            value={user.contactEmail}
            hint="결재 요청·알림을 받는 주소입니다."
            isSaving={isSaving}
            setIsSaving={setIsSaving}
            onSaved={fetchUser}
            showToast={showToast}
            buildSavePayload={(email, verificationToken) => ({
              contactEmail: email,
              contactEmailVerificationToken: verificationToken,
            })}
            clearPayload={{ contactEmail: null }}
          />

          {/* 전화번호 */}
          <div className="py-2 border-t border-line-soft">
            {isEditingPhone ? (
              <div className="space-y-3">
                <PhoneVerification
                  phone={editPhone}
                  onPhoneChange={setEditPhone}
                  onVerified={handlePhoneVerified}
                  disabled={isSaving}
                  skipDuplicateCheck
                />
                <div className="flex justify-end">
                  <Button size="sm" onClick={handleCancelPhoneEdit} disabled={isSaving}>
                    취소
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <dt className="text-sm font-medium text-ink-500">전화번호</dt>
                  <dd className="mt-1 text-sm text-ink-900">
                    {user.phone ? formatPhone(user.phone) : '-'}
                  </dd>
                </div>
                <TextButton
                  onClick={() => {
                    setEditPhone(user.phone?.replace(/-/g, '') || '');
                    setIsEditingPhone(true);
                  }}
                  className="text-sm font-medium"
                >
                  수정
                </TextButton>
              </div>
            )}
          </div>

          {/* 직급 */}
          <div className="flex items-center justify-between py-2 border-t border-line-soft">
            <div className="flex-1">
              <dt className="text-sm font-medium text-ink-500">직급</dt>
              {isEditingRank ? (
                <div className="mt-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editRank}
                      onChange={(e) => setEditRank(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.nativeEvent.isComposing && !isSaving) {
                          handleSaveRank();
                        } else if (e.key === 'Escape') {
                          setIsEditingRank(false);
                          setEditRank(currentMembership?.rank || '');
                        }
                      }}
                      placeholder="예: 대리, 과장"
                      className={`${FORM_CONTROL_CLASS} flex-1`}
                      autoFocus
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveRank}
                      disabled={isSaving || !isRankDirty}
                    >
                      저장
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setIsEditingRank(false);
                        setEditRank(currentMembership?.rank || '');
                      }}
                    >
                      취소
                    </Button>
                  </div>
                  <DirtyHint isDirty={isRankDirty} className="mt-1 block text-xs" />
                </div>
              ) : (
                <dd className="mt-1 text-sm text-ink-900">
                  {currentMembership?.rank || '-'}
                </dd>
              )}
            </div>
            {!isEditingRank && (
              <TextButton
                onClick={() => {
                  setEditRank(currentMembership?.rank || '');
                  setIsEditingRank(true);
                }}
                className="text-sm font-medium"
              >
                수정
              </TextButton>
            )}
          </div>

          {/* 가입일 */}
          <div className="flex items-center justify-between py-2 border-t border-line-soft">
            <div>
              <dt className="text-sm font-medium text-ink-500">가입일</dt>
              <dd className="mt-1 text-sm text-ink-900">
                {formatDate(user.createdAt)}
              </dd>
            </div>
          </div>
        </div>
      </Card>

      {/* 연결된 로그인 수단 섹션 */}
      <Card padding="none" radius="lg" elevation="md">
        <div className="px-6 py-4 border-b border-line">
          <h2 className={CARD_SECTION_TITLE_CLASS}>연결된 로그인 수단</h2>
          <p className="mt-1 text-sm text-ink-500">
            소셜 로그인을 연결하여 다양한 방법으로 로그인할 수 있습니다.
          </p>
        </div>

        {/* 소셜 로그인 연결 버튼 */}
        <div className="px-6 py-4 border-b border-line-soft flex gap-3">
          {!authMethods?.some((m) => m.provider === 'KAKAO') && (
            <button
              onClick={() => {
                const redirectUri = `${window.location.origin}/auth/callback/kakao`;
                const url = authApi.getKakaoAuthUrl(redirectUri);
                if (url) {
                  // state에 link 모드임을 표시
                  const stateUrl = url + `&state=link`;
                  window.location.href = stateUrl;
                } else {
                  showToast('카카오 로그인이 설정되지 않았습니다.', 'error');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-yellow-900 bg-yellow-100 rounded-control hover:bg-yellow-200"
            >
              <span className="w-5 h-5 flex items-center justify-center rounded bg-yellow-400 text-yellow-900 text-xs font-bold">K</span>
              카카오 연결
            </button>
          )}
          {!authMethods?.some((m) => m.provider === 'GOOGLE') && (
            <button
              onClick={() => {
                const redirectUri = `${window.location.origin}/auth/callback/google`;
                const url = authApi.getGoogleAuthUrl(redirectUri);
                if (url) {
                  const stateUrl = url + `&state=link`;
                  window.location.href = stateUrl;
                } else {
                  showToast('구글 로그인이 설정되지 않았습니다.', 'error');
                }
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-control hover:bg-red-200"
            >
              <span className="w-5 h-5 flex items-center justify-center rounded bg-red-500 text-white text-xs font-bold">G</span>
              구글 연결
            </button>
          )}
          {authMethods?.some((m) => m.provider === 'KAKAO') && authMethods?.some((m) => m.provider === 'GOOGLE') && (
            <span className="text-sm text-ink-500">모든 소셜 로그인이 연결되어 있습니다.</span>
          )}
        </div>
        <div className="divide-y divide-line-soft">
          {authMethods?.map((method) => (
            <div key={method.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium ${getProviderStyle(method.provider)}`}
                  >
                    {getProviderLabel(method.provider).charAt(0)}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink-900">
                        {getProviderLabel(method.provider)}
                      </span>
                      {method.verified && (
                        <Badge tone="emerald" shape="rounded">
                          인증됨
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-ink-500">{method.providerIdMasked}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {method.lastUsedAt && (
                    <span className="text-xs text-ink-400">
                      마지막 사용: {formatDate(method.lastUsedAt)}
                    </span>
                  )}
                  {/* 소셜 로그인만 해제 가능, 최소 1개 인증 수단 유지 */}
                  {isSocialAuth(method.provider) && canUnlink && unlinkingAuthId !== method.id && (
                    <TextButton
                      tone="danger"
                      onClick={() => setUnlinkingAuthId(method.id)}
                      className="text-sm"
                    >
                      연결 해제
                    </TextButton>
                  )}
                </div>
              </div>

              {/* 해제 확인 UI */}
              {unlinkingAuthId === method.id && (
                <div className="mt-3 p-3 bg-rose-50 rounded-card border border-rose-200">
                  <p className="text-sm text-rose-700 mb-3">
                    {getProviderLabel(method.provider)} 로그인 연결을 해제하시겠습니까?
                  </p>
                  {hasPasswordAuth && (
                    <div className="mb-3">
                      <label className={FORM_LABEL_CLASS}>비밀번호 확인</label>
                      {/* 위험 구역 안이라 포커스 강조만 rose로 — 그 외 치수·굵기는 FORM_CONTROL_CLASS와
                          같은 값이다. 상수를 문자열 치환하면 Tailwind 정적 스캔이 깨져서 리터럴로 둔다. */}
                      <input
                        type="password"
                        value={unlinkPassword}
                        onChange={(e) => setUnlinkPassword(e.target.value)}
                        placeholder="현재 비밀번호를 입력하세요"
                        className="block w-full h-[42px] rounded-[10px] border border-rose-200 bg-white px-3 text-[14px] font-semibold text-ink-900 placeholder:text-ink-400 placeholder:font-medium focus:outline-none focus:border-rose-500 focus:ring-[3px] focus:ring-rose-100"
                        autoFocus
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleUnlink}
                      disabled={isUnlinking || (hasPasswordAuth && !unlinkPassword)}
                    >
                      {isUnlinking ? '해제 중...' : '연결 해제'}
                    </Button>
                    <Button size="sm" onClick={cancelUnlink} disabled={isUnlinking}>
                      취소
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {(!authMethods || authMethods.length === 0) && (
            <div className="px-6 py-8 text-center text-ink-500 text-sm">
              연결된 로그인 수단이 없습니다.
            </div>
          )}
        </div>
      </Card>

        </div>

        {/* ── 우측 rail: 계정 조작 ── */}
        <div className="space-y-6">

      {/* 비밀번호 변경 섹션 */}
      <Card padding="none" radius="lg" elevation="md">
        <div className="px-6 py-4 border-b border-line">
          <h2 className={CARD_SECTION_TITLE_CLASS}>비밀번호 변경</h2>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-ink-500 mb-4">
            정기적으로 비밀번호를 변경하여 계정을 안전하게 보호하세요.
          </p>
          <Button variant="primary" onClick={() => setIsPasswordModalOpen(true)}>
            비밀번호 변경
          </Button>
        </div>
      </Card>

      {/* 기존 계정 통합 섹션 */}
      <Card padding="none" radius="lg" elevation="md">
        <div className="px-6 py-4 border-b border-line">
          <h2 className={CARD_SECTION_TITLE_CLASS}>기존 계정 통합</h2>
          <p className="mt-1 text-sm text-ink-500">
            다른 방법으로 가입한 기존 계정이 있다면 현재 계정에 통합할 수 있습니다.
          </p>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-ink-600 mb-4">
            통합하면 기존 계정의 모든 데이터(점검, 보고서, 조직 멤버십 등)가 현재 계정으로 이전됩니다.
            통합은 되돌릴 수 없으니 신중하게 진행해주세요.
          </p>
          <Button onClick={() => setIsMergeModalOpen(true)}>기존 계정 통합하기</Button>
        </div>
      </Card>

        </div>
      </div>

      {/* 모달들 */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
      <AccountMergeModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        onSuccess={() => {
          refetchAuthMethods();
          fetchUser();
          // 성공 토스트는 서버에서 ActionResultHelper를 통해 전송됨
        }}
      />
    </div>
  );
}
